// Real data for the analytics.sdics.tech dashboard: every figure comes from
// /api/analytics/* (backend-go/internal/analytics). This file holds the date
// helpers and the two things every page needs first: the counties and the
// range of registration dates in the data.

import { useQuery } from '@tanstack/react-query';
import { analyticsApi, NOT_IN_NRB } from '@services/api/analytics';
import { isVotingOnly } from './agg';

// ---- days ---------------------------------------------------------------------

export const dayLabel = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
export const dayLabelLong = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
export const monthLabel = (m: string) =>
  new Date(`${m.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** A period column from the API ('2026', '2026-04', '2026-04-13') as text. */
export function periodLabel(p: string, by: string): string {
  if (by === 'month' && p.length === 7) return monthLabel(p);
  if (by === 'week' && p.length === 10) return `w/c ${dayLabel(p)}`;
  if (by === 'day' && p.length === 10) return dayLabel(p);
  return p;
}

/** Period labels for a series, the latest one reading "To date". */
export const periodLabels = (periods: string[], by: string) =>
  periods.map((p, i) => (i === periods.length - 1 ? 'To date' : periodLabel(p, by)));

/** Days between two 'YYYY-MM-DD' dates, inclusive. */
export const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

// ---- areas ------------------------------------------------------------------

/** How an area name from the API reads on screen. */
export function areaName(v: string): string {
  if (v === NOT_IN_NRB) return '—'; // registered, but no subcounty/division/location on record
  if (v === 'DIASPORA') return 'Diaspora';
  if (v === 'PRISONS') return 'Prisons';
  return v;
}

/** The 47 counties (Diaspora and Prisons are pending) and the registration date range. */
export function useDataScope() {
  const q = useQuery({
    queryKey: ['analytics', 'options', {}],
    queryFn: () => analyticsApi.options({}),
    staleTime: 30 * 60 * 1000,
  });
  return {
    counties: (q.data?.values ?? []).filter((c) => !isVotingOnly(c)),
    first: q.data?.date_from ?? '',
    last: q.data?.date_to ?? '',
    loading: q.isLoading,
    error: q.error,
  };
}
